// Admin Daily Schedule - plan, view and manage coach visits across all
// Balgruhas for a two-week period. Left pane ("child view"): every centre with
// its children, visit counts and computers. Right pane ("coach view"): the
// visits per day. A draggable centre line resizes or maximises either pane.
// Visits are the existing schedules (/api/schedules), so they also show up on
// the admin and coach dashboards' existing Daily Schedule views.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MapPin,
  MoreVertical,
  Plus,
} from "lucide-react";
import { deleteSchedule, getBalagruha, getSchedules, updateSchedule } from "../../api";
import loadAllUsers from "../../components/adminDailySchedule/loadAllUsers";
import showToast from "../../utils/toast";
import ResizableSplit from "../../components/adminDailySchedule/ResizableSplit";
import VisitModal from "../../components/adminDailySchedule/VisitModal";
import ConfirmDialog from "../../components/adminDailySchedule/ConfirmDialog";
import useFixedHeaderOverlap from "../../components/adminDailySchedule/useFixedHeaderOverlap";
import {
  CENTRE_COLORS,
  DAYS_PER_WEEK,
  PERIOD_WEEKS,
  VISIT_STATUS_LABELS,
  buildPeriodWeeks,
  countStudentsByCentre,
  describeScheduleError,
  flattenScheduleGroups,
  formatClock12,
  formatClockShort,
  formatDateKeyLong,
  formatPeriodLabel,
  formatWeekLabel,
  getId,
  getVisitStatus,
  isCoachUser,
  shiftDateKey,
  sortByStartTime,
  startOfWeekMonday,
  toDateKey,
} from "../../components/adminDailySchedule/scheduleUtils";
import "../../components/adminDailySchedule/AdminDailySchedule.css";
import SearchableSelect from "../../components/common/SearchableSelect";

const MIN_SLOTS_PER_DAY = 4;
const SPLIT_STORAGE_KEY = "adminDailySchedule.childViewWidth";
const DEFAULT_CHILD_VIEW_PERCENT = 25;

// Legend at the bottom; each entry also shows/hides that status in the calendar.
export const LESSON_PLAN_PATH = "/admin/daily-schedule/lesson-plan";

const STATUS_LEGEND = [
  { status: "completed", label: "Completed" },
  { status: "scheduled", label: "Scheduled (Active)" },
  { status: "cancelled", label: "Cancelled" },
];

const visitCentreName = (visit, centresById) =>
  visit.balagruha?.name || centresById[getId(visit.balagruhaId)]?.name || "Balgruha";

const visitCoachName = (visit) => visit.assignedToUser?.name || "Coach";

const visitTimeRange = (visit) =>
  `${formatClockShort(visit.startTime)} – ${formatClockShort(visit.endTime)}`;

function CentreTable({ centres, stats, loading, selectedCentreId, onSelectCentre }) {
  return (
    <div className="ads-centres">
      <table className="ads-centre-table">
        <colgroup>
          <col />
          <col className="ads-col-children" />
          <col className="ads-col-visits" />
          <col className="ads-col-computers" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Centre Name</th>
            <th scope="col">No. of Children</th>
            <th scope="col">
              <abbr title="Visits in the first week shown / visits in the whole two-week period (cancelled visits are not counted)">
                Visits This Week / Total Visits
              </abbr>
            </th>
            <th scope="col">No. of Computers</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={4} className="ads-table-message">Loading centres...</td>
            </tr>
          )}
          {!loading && centres.length === 0 && (
            <tr>
              <td colSpan={4} className="ads-table-message">No Balgruhas found.</td>
            </tr>
          )}
          {!loading &&
            centres.map((centre) => {
              const centreStats = stats[centre._id] || { children: 0, week: 0, total: 0, computers: 0 };
              const isSelected = selectedCentreId === centre._id;
              return (
                <tr key={centre._id} className={isSelected ? "is-selected" : ""}>
                  <th scope="row">
                    <button
                      type="button"
                      className="ads-centre-name"
                      aria-pressed={isSelected}
                      title={isSelected ? "Show all Balgruhas" : `Show only ${centre.name}`}
                      onClick={() => onSelectCentre(isSelected ? "" : centre._id)}
                    >
                      <MapPin size={20} fill={centre.color} color={centre.color} className="ads-centre-pin" aria-hidden="true" />
                      <span>{centre.name}</span>
                    </button>
                  </th>
                  <td>{centreStats.children}</td>
                  <td>
                    {centreStats.week}/{centreStats.total}
                  </td>
                  <td>{centreStats.computers}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

function VisitCard({ visit, centreName, menuOpen, onToggleMenu, onEdit, onSetStatus, onCancel, onDelete, onOpenLessonPlan }) {
  const status = getVisitStatus(visit.status);
  const coachName = visitCoachName(visit);
  const timeRange = visitTimeRange(visit);
  const fullTime = `${formatClock12(visit.startTime)} – ${formatClock12(visit.endTime)}`;

  const runAction = (action) => {
    onToggleMenu(null);
    action(visit);
  };

  return (
    <div className={`ads-visit ads-visit-${status}`}>
      <button
        type="button"
        className="ads-visit-main"
        onClick={() => onEdit(visit)}
        title={`${centreName} · ${coachName} · ${fullTime} · ${VISIT_STATUS_LABELS[status]}`}
        aria-label={`Edit visit: ${centreName}, ${coachName}, ${fullTime}, ${VISIT_STATUS_LABELS[status]}`}
      >
        <span className="ads-visit-icon" aria-hidden="true">
          {status === "completed" ? <CheckCircle2 size={18} /> : <MapPin size={17} />}
        </span>
        <span className="ads-visit-text">
          <span className="ads-visit-centre">{centreName}</span>
          <span className="ads-visit-coach">{coachName}</span>
          <span className="ads-visit-time">{timeRange}</span>
          {status === "cancelled" && <span className="ads-visit-flag">Cancelled</span>}
        </span>
      </button>

      <div className="ads-menu-wrap">
        <button
          type="button"
          className="ads-visit-menu-button"
          aria-label={`Actions for ${centreName} visit`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => onToggleMenu(menuOpen ? null : visit._id)}
        >
          <MoreVertical size={16} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className="ads-menu" role="menu">
            <button type="button" role="menuitem" onClick={() => runAction(onEdit)}>
              Edit visit
            </button>
            {visit.lessonPlanId && (
              <button type="button" role="menuitem" onClick={() => runAction(onOpenLessonPlan)}>
                Open lesson plan
              </button>
            )}
            {status === "scheduled" && (
              <button type="button" role="menuitem" onClick={() => runAction((item) => onSetStatus(item, "completed"))}>
                Mark as completed
              </button>
            )}
            {status !== "scheduled" && (
              <button type="button" role="menuitem" onClick={() => runAction((item) => onSetStatus(item, "pending"))}>
                Mark as scheduled
              </button>
            )}
            {status !== "cancelled" && (
              <button type="button" role="menuitem" onClick={() => runAction(onCancel)}>
                Cancel visit
              </button>
            )}
            <button type="button" role="menuitem" className="is-danger" onClick={() => runAction(onDelete)}>
              Delete visit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDailySchedule() {
  const navigate = useNavigate();
  const todayKey = toDateKey(new Date());
  const [periodStart, setPeriodStart] = useState(() => toDateKey(startOfWeekMonday(new Date())));
  const [centres, setCentres] = useState([]);
  const [users, setUsers] = useState([]);
  const [baseLoading, setBaseLoading] = useState(true);
  const [baseError, setBaseError] = useState("");
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState("");
  const [centreFilter, setCentreFilter] = useState("");
  const [coachFilter, setCoachFilter] = useState("");
  const [hiddenStatuses, setHiddenStatuses] = useState([]);
  const [visitModal, setVisitModal] = useState(null); // { mode: "create" | "edit", visit?, defaults? }
  const [confirm, setConfirm] = useState(null); // { kind: "cancelSchedule" | "delete", visit, fromModal }
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const periodPickerRef = useRef(null);
  const visitsRequestRef = useRef(0);
  const pageRef = useRef(null);
  const headerOverlap = useFixedHeaderOverlap(pageRef);

  const periodEnd = shiftDateKey(periodStart, PERIOD_WEEKS * DAYS_PER_WEEK - 1);
  const weeks = useMemo(() => buildPeriodWeeks(periodStart), [periodStart]);

  const loadBaseData = useCallback(async () => {
    setBaseLoading(true);
    setBaseError("");
    const [centreResult, userResult] = await Promise.allSettled([getBalagruha(), loadAllUsers()]);
    const problems = [];
    if (centreResult.status === "fulfilled") {
      const list = centreResult.value?.data?.balagruhas || [];
      setCentres(list.map((centre, index) => ({ ...centre, color: CENTRE_COLORS[index % CENTRE_COLORS.length] })));
    } else {
      problems.push("Balgruhas");
    }
    if (userResult.status === "fulfilled") {
      setUsers(userResult.value);
    } else {
      problems.push("coaches and children");
    }
    if (problems.length > 0) setBaseError(`Could not load ${problems.join(" or ")}.`);
    setBaseLoading(false);
  }, []);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  const centreIdsKey = centres.map((centre) => centre._id).join(",");

  const loadVisits = useCallback(async () => {
    const requestId = visitsRequestRef.current + 1;
    visitsRequestRef.current = requestId;
    const balagruhaIds = centreIdsKey ? centreIdsKey.split(",") : [];
    if (balagruhaIds.length === 0) {
      setVisits([]);
      setVisitsLoading(false);
      return;
    }
    setVisitsLoading(true);
    setVisitsError("");
    try {
      const response = await getSchedules({
        balagruhaIds,
        startDate: periodStart,
        endDate: periodEnd,
        status: [],
      });
      if (requestId !== visitsRequestRef.current) return;
      setVisits(flattenScheduleGroups(response?.data?.schedules));
    } catch (error) {
      if (requestId !== visitsRequestRef.current) return;
      setVisits([]);
      setVisitsError("Could not load visits for this period.");
    } finally {
      if (requestId === visitsRequestRef.current) setVisitsLoading(false);
    }
  }, [centreIdsKey, periodStart, periodEnd]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  // Close the open card menu / create menu on an outside click or Escape.
  useEffect(() => {
    if (!openMenuId && !createMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (event.target.closest(".ads-menu-wrap")) return;
      setOpenMenuId(null);
      setCreateMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpenMenuId(null);
      setCreateMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId, createMenuOpen]);

  const centresById = useMemo(
    () => Object.fromEntries(centres.map((centre) => [centre._id, centre])),
    [centres]
  );

  const coaches = useMemo(
    () =>
      users
        .filter((user) => isCoachUser(user) && user.status !== "inactive")
        .map((user) => ({ _id: user._id, name: user.name || user.email || "Coach" }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  // Coach filter also lists anyone who has a visit in this period.
  const coachFilterOptions = useMemo(() => {
    const options = [...coaches];
    visits.forEach((visit) => {
      const assigneeId = getId(visit.assignedTo);
      if (assigneeId && !options.some((option) => option._id === assigneeId)) {
        options.push({ _id: assigneeId, name: visitCoachName(visit) });
      }
    });
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [coaches, visits]);

  const coachScopedVisits = useMemo(
    () => visits.filter((visit) => !coachFilter || getId(visit.assignedTo) === coachFilter),
    [visits, coachFilter]
  );

  const scopedVisits = useMemo(
    () => coachScopedVisits.filter((visit) => !centreFilter || getId(visit.balagruhaId) === centreFilter),
    [coachScopedVisits, centreFilter]
  );

  const statusCounts = useMemo(() => {
    const counts = { completed: 0, scheduled: 0, cancelled: 0 };
    scopedVisits.forEach((visit) => {
      counts[getVisitStatus(visit.status)] += 1;
    });
    return counts;
  }, [scopedVisits]);

  const shownVisits = useMemo(
    () => scopedVisits.filter((visit) => !hiddenStatuses.includes(getVisitStatus(visit.status))),
    [scopedVisits, hiddenStatuses]
  );

  const toggleStatus = (status) =>
    setHiddenStatuses((current) =>
      current.includes(status) ? current.filter((value) => value !== status) : [...current, status]
    );

  const visitsByDay = useMemo(() => {
    const byDay = {};
    sortByStartTime(shownVisits).forEach((visit) => {
      (byDay[visit.dateKey] = byDay[visit.dateKey] || []).push(visit);
    });
    return byDay;
  }, [shownVisits]);

  const centreStats = useMemo(() => {
    const children = countStudentsByCentre(users);
    const firstWeekKeys = new Set(weeks[0].days.map((day) => day.key));
    const stats = {};
    centres.forEach((centre) => {
      stats[centre._id] = {
        children: children[centre._id] || 0,
        week: 0,
        total: 0,
        computers: Array.isArray(centre.assignedMachines) ? centre.assignedMachines.length : 0,
      };
    });
    coachScopedVisits.forEach((visit) => {
      const centreStatsEntry = stats[getId(visit.balagruhaId)];
      if (!centreStatsEntry || getVisitStatus(visit.status) === "cancelled") return;
      centreStatsEntry.total += 1;
      if (firstWeekKeys.has(visit.dateKey)) centreStatsEntry.week += 1;
    });
    return stats;
  }, [centres, users, weeks, coachScopedVisits]);

  const openCreate = (dateKey) => {
    setCreateMenuOpen(false);
    setVisitModal({
      mode: "create",
      defaults: {
        // Today, or the first day shown when looking at a future period.
        dateKey: dateKey || (periodStart > todayKey ? periodStart : todayKey),
        centreIds: centreFilter ? [centreFilter] : [],
        coachId: coaches.some((coach) => coach._id === coachFilter) ? coachFilter : "",
      },
    });
  };

  // "Create Lesson Plan" opens its own page, prefilled from the filters.
  const openLessonPlan = (planId) => {
    if (planId) {
      navigate(`${LESSON_PLAN_PATH}?id=${planId}`);
      return;
    }
    const params = new URLSearchParams({ date: periodStart > todayKey ? periodStart : todayKey });
    if (centreFilter) params.set("balagruhaId", centreFilter);
    if (coaches.some((coach) => coach._id === coachFilter)) params.set("coachId", coachFilter);
    navigate(`${LESSON_PLAN_PATH}?${params.toString()}`);
  };

  const openEdit = (visit) => {
    setOpenMenuId(null);
    setVisitModal({ mode: "edit", visit });
  };

  const closeVisitModal = useCallback(() => setVisitModal(null), []);

  const handleVisitSaved = (message) => {
    showToast(message, "success");
    setVisitModal(null);
    loadVisits();
  };

  const setVisitStatus = async (visit, status) => {
    try {
      await updateSchedule(
        {
          balagruhaId: getId(visit.balagruhaId),
          assignedTo: getId(visit.assignedTo),
          date: visit.date,
          startTime: visit.startTime,
          endTime: visit.endTime,
          status,
        },
        visit._id
      );
      const messages = {
        completed: "Visit marked as completed",
        pending: "Visit marked as scheduled",
        cancelled: "Visit cancelled",
      };
      showToast(messages[status] || "Visit updated", "success");
      loadVisits();
      return true;
    } catch (error) {
      showToast(describeScheduleError(error, "Failed to update the visit"), "error");
      return false;
    }
  };

  const removeVisit = async (visit) => {
    try {
      await deleteSchedule(visit._id);
      showToast("Visit deleted successfully", "success");
      loadVisits();
      return true;
    } catch (error) {
      showToast(error?.response?.data?.message || "Failed to delete the visit", "error");
      return false;
    }
  };

  const runConfirmAction = async (action) => {
    const fromModal = confirm?.fromModal;
    setConfirmBusy(true);
    const succeeded = await action(confirm.visit);
    setConfirmBusy(false);
    if (!succeeded) return;
    setConfirm(null);
    if (fromModal) setVisitModal(null);
  };

  const closeConfirm = useCallback(() => setConfirm(null), []);

  const confirmContent = (() => {
    if (!confirm) return null;
    const { visit, kind } = confirm;
    const summary = `${visitCentreName(visit, centresById)} with ${visitCoachName(visit)} on ${formatDateKeyLong(visit.dateKey)}, ${formatClock12(visit.startTime)} – ${formatClock12(visit.endTime)}`;
    const keep = { label: "Keep visit", variant: "outline", onClick: closeConfirm };
    const remove = {
      label: "Delete permanently",
      variant: "danger",
      onClick: () => runConfirmAction(removeVisit),
    };
    if (kind === "delete") {
      return {
        title: "Delete this visit?",
        message: `${summary}. This removes it from every schedule and cannot be undone.`,
        actions: [keep, remove],
      };
    }
    if (getVisitStatus(visit.status) === "cancelled") {
      return {
        title: "This visit is already cancelled",
        message: `${summary}. You can delete it permanently, or keep it on the schedule as cancelled.`,
        actions: [keep, remove],
      };
    }
    return {
      title: "Cancel this visit?",
      message: `${summary}. Mark it as cancelled to keep it on the schedule, or delete it permanently.`,
      actions: [
        keep,
        remove,
        {
          label: "Mark as cancelled",
          variant: "primary",
          onClick: () => runConfirmAction((item) => setVisitStatus(item, "cancelled")),
        },
      ],
    };
  })();

  const openPeriodPicker = () => {
    const input = periodPickerRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
        return;
      }
    } catch (error) {
      // Fall through to focusing the input.
    }
    input.focus();
    input.click();
  };

  const handleExport = () => {
    const visitRows = [...shownVisits]
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey) || new Date(a.startTime) - new Date(b.startTime))
      .map((visit) => ({
        Date: formatDateKeyLong(visit.dateKey),
        Day: weeks.flatMap((week) => week.days).find((day) => day.key === visit.dateKey)?.weekday || "",
        Balgruha: visitCentreName(visit, centresById),
        Coach: visitCoachName(visit),
        "Start Time": formatClock12(visit.startTime),
        "End Time": formatClock12(visit.endTime),
        Status: VISIT_STATUS_LABELS[getVisitStatus(visit.status)],
      }));
    const centreRows = centres.map((centre) => ({
      "Centre Name": centre.name,
      "No. of Children": centreStats[centre._id]?.children || 0,
      "Visits This Week": centreStats[centre._id]?.week || 0,
      "Total Visits": centreStats[centre._id]?.total || 0,
      "No. of Computers": centreStats[centre._id]?.computers || 0,
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(visitRows, {
        header: ["Date", "Day", "Balgruha", "Coach", "Start Time", "End Time", "Status"],
      }),
      "Visits"
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(centreRows, {
        header: ["Centre Name", "No. of Children", "Visits This Week", "Total Visits", "No. of Computers"],
      }),
      "Centres"
    );
    XLSX.writeFile(workbook, `daily-schedule-${periodStart}-to-${periodEnd}.xlsx`);
  };

  const childView = (
    <CentreTable
      centres={centres}
      stats={centreStats}
      loading={baseLoading}
      selectedCentreId={centreFilter}
      onSelectCentre={setCentreFilter}
    />
  );

  const coachView = (
    <div className="ads-calendar" aria-busy={visitsLoading}>
      {visitsLoading && (
        <div className="ads-calendar-status" role="status">
          Loading visits...
        </div>
      )}
      {visitsError && (
        <div className="ads-banner ads-banner-error" role="alert">
          <span>{visitsError}</span>
          <button type="button" className="ads-link-button" onClick={loadVisits}>
            Retry
          </button>
        </div>
      )}
      {weeks.map((week) => {
        const firstDay = week.days[0];
        const lastDay = week.days[week.days.length - 1];
        const busiestDay = Math.max(0, ...week.days.map((day) => (visitsByDay[day.key] || []).length));
        const slotsPerDay = Math.max(MIN_SLOTS_PER_DAY, busiestDay + 1);
        const headingId = `ads-week-${week.index}`;
        return (
          <section key={firstDay.key} className="ads-week" aria-labelledby={headingId}>
            <div className="ads-week-header">
              <button
                type="button"
                className="ads-icon-button"
                onClick={() => setPeriodStart((key) => shiftDateKey(key, -DAYS_PER_WEEK))}
                aria-label="Previous week"
              >
                <ChevronLeft size={20} aria-hidden="true" />
              </button>
              <h2 id={headingId}>
                Week {week.index + 1}
                <span className="ads-week-range">{formatWeekLabel(firstDay.key, lastDay.key)}</span>
              </h2>
              <button
                type="button"
                className="ads-icon-button ads-week-next"
                onClick={() => setPeriodStart((key) => shiftDateKey(key, DAYS_PER_WEEK))}
                aria-label="Next week"
              >
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="ads-week-grid">
              {week.days.map((day) => {
                const dayVisits = visitsByDay[day.key] || [];
                const isPast = day.key < todayKey;
                const freeSlots = Math.max(0, slotsPerDay - dayVisits.length);
                return (
                  <div
                    key={day.key}
                    className={`ads-day${day.key === todayKey ? " is-today" : ""}${isPast ? " is-past" : ""}`}
                    role="group"
                    aria-label={`${day.weekday} ${day.label}`}
                  >
                    <div className="ads-day-head">
                      <span className="ads-day-weekday">{day.weekday}</span>
                      <span className="ads-day-date">{day.label}</span>
                    </div>
                    <div className="ads-day-slots">
                      {dayVisits.map((visit) => (
                        <VisitCard
                          key={visit._id}
                          visit={visit}
                          centreName={visitCentreName(visit, centresById)}
                          menuOpen={openMenuId === visit._id}
                          onToggleMenu={setOpenMenuId}
                          onEdit={openEdit}
                          onSetStatus={setVisitStatus}
                          onCancel={(item) => setVisitStatus(item, "cancelled")}
                          onDelete={(item) => setConfirm({ kind: "delete", visit: item, fromModal: false })}
                          onOpenLessonPlan={(item) => openLessonPlan(item.lessonPlanId)}
                        />
                      ))}
                      {Array.from({ length: freeSlots }, (_, slotIndex) =>
                        isPast ? (
                          <div key={`empty-${slotIndex}`} className="ads-slot-empty" aria-hidden="true" />
                        ) : (
                          <button
                            key={`add-${slotIndex}`}
                            type="button"
                            className="ads-add-visit"
                            onClick={() => openCreate(day.key)}
                            aria-label={`Add visit on ${day.weekday} ${day.label}`}
                          >
                            <Plus size={18} aria-hidden="true" />
                            Add Visit
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );

  return (
    <div ref={pageRef} className="ads-page" style={{ "--ads-header-overlap": `${headerOverlap}px` }}>
      <header className="ads-header">
        <div className="ads-title">
          <span className="ads-title-icon" aria-hidden="true">
            <CalendarDays size={30} />
          </span>
          <div>
            <h1>Daily Schedule</h1>
            <p>Plan, view and manage your visits across Balagruhas and Varkari Sansthas</p>
          </div>
        </div>

        <div className="ads-filters">
          <label className="ads-filter">
            <span>Balgruha</span>
            <SearchableSelect searchPlaceholder="Search balagruha..." value={centreFilter} onChange={(event) => setCentreFilter(event.target.value)}>
              <option value="">All Balagruhas</option>
              {centres.map((centre) => (
                <option key={centre._id} value={centre._id}>
                  {centre.name}
                </option>
              ))}
            </SearchableSelect>
          </label>
          <label className="ads-filter">
            <span>Coach</span>
            <SearchableSelect searchPlaceholder="Search coach..." value={coachFilter} onChange={(event) => setCoachFilter(event.target.value)}>
              <option value="">All Coaches</option>
              {coachFilterOptions.map((coach) => (
                <option key={coach._id} value={coach._id}>
                  {coach.name}
                </option>
              ))}
            </SearchableSelect>
          </label>
        </div>

        <div className="ads-period-nav">
          <button
            type="button"
            className="ads-icon-button"
            onClick={() => setPeriodStart((key) => shiftDateKey(key, -PERIOD_WEEKS * DAYS_PER_WEEK))}
            aria-label="Previous two weeks"
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="ads-period-label"
            onClick={openPeriodPicker}
            title="Choose the first day to show"
          >
            <Calendar size={20} aria-hidden="true" />
            <span>{formatPeriodLabel(periodStart, periodEnd)}</span>
          </button>
          <input
            ref={periodPickerRef}
            type="date"
            className="ads-period-input"
            value={periodStart}
            onChange={(event) => event.target.value && setPeriodStart(event.target.value)}
            aria-label="First day to show"
            tabIndex={-1}
          />
          <button
            type="button"
            className="ads-icon-button"
            onClick={() => setPeriodStart((key) => shiftDateKey(key, PERIOD_WEEKS * DAYS_PER_WEEK))}
            aria-label="Next two weeks"
          >
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="ads-create ads-menu-wrap">
          <button type="button" className="ads-create-main" onClick={() => openLessonPlan()}>
            <Plus size={20} aria-hidden="true" />
            Create Lesson Plan
          </button>
          <button
            type="button"
            className="ads-create-toggle"
            aria-label="More create options"
            aria-haspopup="menu"
            aria-expanded={createMenuOpen}
            onClick={() => setCreateMenuOpen((open) => !open)}
          >
            <ChevronDown size={18} aria-hidden="true" />
          </button>
          {createMenuOpen && (
            <div className="ads-menu ads-create-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => openCreate(todayKey)}>
                Add a visit for today
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setCreateMenuOpen(false);
                  handleExport();
                }}
              >
                Export this schedule
              </button>
            </div>
          )}
        </div>
      </header>

      {baseError && (
        <div className="ads-banner ads-banner-error" role="alert">
          <span>{baseError}</span>
          <button type="button" className="ads-link-button" onClick={loadBaseData}>
            Retry
          </button>
        </div>
      )}

      <ResizableSplit
        left={childView}
        right={coachView}
        leftLabel="child view"
        rightLabel="coach view"
        defaultLeftPercent={DEFAULT_CHILD_VIEW_PERCENT}
        storageKey={SPLIT_STORAGE_KEY}
      />

      <footer className="ads-footer">
        <div className="ads-legend" role="group" aria-label="Show visits by status">
          {STATUS_LEGEND.map(({ status, label }) => {
            const isShown = !hiddenStatuses.includes(status);
            return (
              <button
                key={status}
                type="button"
                className={`ads-legend-item is-${status}${isShown ? "" : " is-off"}`}
                aria-pressed={isShown}
                title={`${isShown ? "Hide" : "Show"} ${label.toLowerCase()} visits`}
                onClick={() => toggleStatus(status)}
              >
                <span className="ads-legend-dot" aria-hidden="true" />
                <span>{label}</span>
                <span className="ads-legend-count">{statusCounts[status]}</span>
              </button>
            );
          })}
          {hiddenStatuses.length > 0 && (
            <button type="button" className="ads-link-button ads-legend-reset" onClick={() => setHiddenStatuses([])}>
              Show all
            </button>
          )}
        </div>
        <button type="button" className="ads-button ads-button-outline ads-export" onClick={handleExport}>
          <Download size={20} aria-hidden="true" />
          Export
        </button>
      </footer>

      {visitModal && (
        <VisitModal
          key={visitModal.visit?._id || "new"}
          mode={visitModal.mode}
          visit={visitModal.visit}
          defaults={visitModal.defaults}
          centres={centres}
          coaches={coaches}
          onClose={closeVisitModal}
          onSaved={handleVisitSaved}
          onRequestCancelSchedule={(visit) => setConfirm({ kind: "cancelSchedule", visit, fromModal: true })}
          isBlocked={Boolean(confirm)}
        />
      )}

      {confirmContent && (
        <ConfirmDialog
          title={confirmContent.title}
          message={confirmContent.message}
          actions={confirmContent.actions}
          busy={confirmBusy}
          onClose={closeConfirm}
        />
      )}
    </div>
  );
}
